/**
 * Pure chat-transcript grouping logic (F — richer conversation transcript).
 *
 * Derives per-message rendering hints so a transcript can visually cluster
 * consecutive same-direction messages ("group") the way IG/WhatsApp-style
 * threads do: a shared avatar, a single trailing timestamp, and a date
 * divider when the conversation crosses midnight.
 *
 * No DOM, no i18n, no service types — generic over any timestamped/directed
 * shape so it stays reusable outside `ConversationDetailPage`.
 */

export interface TimestampedMessage {
  direction: 'inbound' | 'outbound' | 'owner';
  timestamp: string;
}

export interface MessageGroupInfo {
  /** True when this is the first message of a new visual group. */
  isGroupStart: boolean;
  /** True when this is the last message of its visual group (timestamp goes here). */
  isGroupEnd: boolean;
  /** True when a date divider should render immediately before this message. */
  showDateSeparator: boolean;
}

const GROUP_WINDOW_MS = 5 * 60 * 1000;

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function withinGroupWindow(a: Date, b: Date): boolean {
  return Math.abs(a.getTime() - b.getTime()) <= GROUP_WINDOW_MS;
}

/**
 * `messages` must already be in chronological (render) order — this does
 * not sort. Returns a new array; input is untouched.
 */
export function groupMessages<T extends TimestampedMessage>(
  messages: T[]
): Array<T & MessageGroupInfo> {
  return messages.map((message, idx) => {
    const prev = messages[idx - 1];
    const next = messages[idx + 1];
    const cur = new Date(message.timestamp);

    const continuesFromPrev =
      prev !== undefined &&
      prev.direction === message.direction &&
      sameCalendarDay(cur, new Date(prev.timestamp)) &&
      withinGroupWindow(cur, new Date(prev.timestamp));

    const continuesToNext =
      next !== undefined &&
      next.direction === message.direction &&
      sameCalendarDay(cur, new Date(next.timestamp)) &&
      withinGroupWindow(cur, new Date(next.timestamp));

    const showDateSeparator = prev !== undefined && !sameCalendarDay(cur, new Date(prev.timestamp));

    return {
      ...message,
      isGroupStart: !continuesFromPrev,
      isGroupEnd: !continuesToNext,
      showDateSeparator,
    };
  });
}
